// Brands.js - City Removed

import React, { useState, useEffect, useRef } from 'react';
import {
  FaTags,
  FaSearch,
  FaPlus,
  FaTrash,
  FaEdit,
  FaCheckDouble,
  FaEllipsisV,
  FaChevronDown,
  FaChevronRight
} from 'react-icons/fa';
import { TbBrandCodepen } from 'react-icons/tb';
import { RiDeleteBin5Line, RiDeleteBinLine } from "react-icons/ri";
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import './Brands.css';

// Alerts always open ABOVE the Add/Edit modal (otherwise they hide behind it
// and only show up after the modal is closed).
const SwalTop = Swal.mixin({
  didOpen: () => {
    const container = Swal.getContainer();
    if (container) container.style.zIndex = '99999';
  }
});

// Last loaded brands of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const BRANDS_CACHE_PREFIX = 'brands_cache_';

const readBrandsCache = (id) => {
  if (!id || id === 'all') return null;
  try {
    const raw = localStorage.getItem(`${BRANDS_CACHE_PREFIX}${id}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeBrandsCache = (id, data) => {
  if (!id || id === 'all') return;
  try {
    localStorage.setItem(`${BRANDS_CACHE_PREFIX}${id}`, JSON.stringify(data));
  } catch (e) {}
};

const Brands = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [brandName, setBrandName] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [shopName, setShopName] = useState('');
  const [selectedShopId, setSelectedShopId] = useState('');
  const [brands, setBrands] = useState(() => readBrandsCache(shopId) || []);
  const [shops, setShops] = useState([]);
  const [implantCompanies, setImplantCompanies] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [shopMap, setShopMap] = useState({});
  const [ukShopId, setUkShopId] = useState(null);
  const [isUkShopSet, setIsUkShopSet] = useState(false);

  // Move To (copy to other shops)
  const [showMoveList, setShowMoveList] = useState(false);
  const [moveShopIds, setMoveShopIds] = useState([]);
  const [moveLoading, setMoveLoading] = useState(false);

  // True only while there is nothing to show yet (so "No brands found" never flashes)
  const [isLoading, setIsLoading] = useState(() => !readBrandsCache(shopId));

  const companyInputRef = useRef(null);
  const companyDropdownRef = useRef(null);
  const brandsRequestRef = useRef(0);

  const API_BASE_URL = 'https://super-backend.hira-tech.co.uk/api/product';

  const isAllShopsMode = shopId === 'all';
  const isNoShopId = !shopId;

  const showSuccessAlert = (message) => {
    SwalTop.fire({
      icon: 'success',
      title: 'Success!',
      text: message,
      timer: 2000,
      showConfirmButton: true,
      confirmButtonColor: '#3085d6'
    });
  };

  const showErrorAlert = (message) => {
    SwalTop.fire({
      icon: 'error',
      title: 'Error!',
      text: message,
      confirmButtonColor: '#d33'
    });
  };

  const showWarningAlert = (message) => {
    SwalTop.fire({
      icon: 'warning',
      title: 'Warning!',
      text: message,
      confirmButtonColor: '#f0ad4e'
    });
  };

  const showDeleteConfirmation = (brandName, isMultiple = false, count = 1) => {
    const title = isMultiple
      ? `Are you sure you want to delete ${count} brand(s)?`
      : `Are you sure you want to delete brand "${brandName}"?`;

    return SwalTop.fire({
      title: 'Are you sure?',
      text: title,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });
  };

  const findUkShop = (shopsList) => {
    const ukShop = shopsList.find(shop =>
      shop.shop_name?.toLowerCase().includes('uk')
    );
    return ukShop || shopsList[0];
  };

  const fetchShops = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/shops/with-details/`);
      if (response.ok) {
        const data = await response.json();
        const shopsArray = Array.isArray(data) ? data : [];
        setShops(shopsArray);

        const shopMapping = {};
        shopsArray.forEach(shop => {
          shopMapping[shop.shop_id] = shop.shop_name;
        });
        setShopMap(shopMapping);

        const ukShop = findUkShop(shopsArray);
        if (ukShop) {
          setUkShopId(ukShop.shop_id);
          if (!shopId || shopId === 'all') {
            navigate(`/shop/${ukShop.shop_id}/brands`, { replace: true });
            setIsUkShopSet(true);
          }
        }
        return shopsArray;
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  const fetchImplantCompanies = async (targetShopId = null) => {
    try {
      let url = `${API_BASE_URL}/implant-companies/`;
      const currentShopId = targetShopId || (shopId && shopId !== 'all' ? shopId : ukShopId);

      if (currentShopId) {
        url += `?shop_id=${currentShopId}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setImplantCompanies(Array.isArray(data) ? data : []);
        return data;
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  const fetchBrands = async () => {
    // Only the latest request is allowed to update the screen
    const requestId = ++brandsRequestRef.current;

    try {
      let url = `${API_BASE_URL}/brands/`;

      let targetShopId = null;
      if (shopId && shopId !== 'all') {
        targetShopId = shopId;
      } else if (ukShopId) {
        targetShopId = ukShopId;
      }

      if (targetShopId) {
        url += `?shop_id=${targetShopId}`;
      }

      const response = await fetch(url);
      if (requestId !== brandsRequestRef.current) return;

      if (response.ok) {
        const data = await response.json();
        if (requestId !== brandsRequestRef.current) return;

        const brandsData = Array.isArray(data) ? data : [];
        setBrands(brandsData);
        writeBrandsCache(targetShopId, brandsData);
        setIsLoading(false); // list is ready: show it right away

        if (shopId && shopId !== 'all') {
          if (brandsData.length > 0 && brandsData[0].shop) {
            setShopName(brandsData[0].shop.shop_name);
          } else {
            try {
              const shopResponse = await fetch(`${API_BASE_URL}/shops/${shopId}/`);
              if (shopResponse.ok) {
                const shopData = await shopResponse.json();
                setShopName(shopData.shop_name);
              }
            } catch (err) {}
          }
        } else if (ukShopId) {
          const ukShop = shops.find(s => s.shop_id === ukShopId);
          if (ukShop) setShopName(ukShop.shop_name);
        }
      } else {
        setBrands([]);
        setIsLoading(false);
      }
    } catch (error) {
      if (requestId !== brandsRequestRef.current) return;
      setBrands([]);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      if (shopId && shopId !== 'all') {
        // Show the saved list of this shop immediately (if there is one)
        const cached = readBrandsCache(shopId);
        if (cached) {
          setBrands(cached);
          setIsLoading(false);
        } else {
          setBrands([]);
          setIsLoading(true);
        }

        // Everything loads together instead of one after another
        await Promise.all([
          fetchBrands(),
          fetchImplantCompanies(shopId),
          fetchShops()
        ]);
      } else {
        // No shop in the URL yet: fetchShops redirects to the shop,
        // and this effect runs again with the new shopId.
        const shopsData = await fetchShops();
        if (!shopsData || shopsData.length === 0) {
          setIsLoading(false);
        }
      }
    };
    loadInitialData();
  }, [shopId]);

  const filteredCompanies = implantCompanies.filter(company =>
    company.company_name?.toLowerCase().includes(companySearchTerm.toLowerCase())
  );

  const handleSearch = (e) => setSearchTerm(e.target.value);

  const filteredBrands = brands.filter(brand =>
    brand.brand_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSelectedCompanyName = () => {
    if (!selectedCompanyId) return '';
    const company = implantCompanies.find(c => c.company_id === parseInt(selectedCompanyId));
    return company ? company.company_name : '';
  };

  const getShopDisplayName = (brand) => {
    if (brand.shop && brand.shop.shop_name) return brand.shop.shop_name;
    if (brand.shop_id && shopMap[brand.shop_id]) return shopMap[brand.shop_id];
    if (brand.shop_id) return `Shop ${brand.shop_id}`;
    return 'No Shop';
  };

  const getCompanyDisplayName = (brand) => {
    if (brand.implant_company && brand.implant_company.company_name) {
      return brand.implant_company.company_name;
    }
    return '-';
  };

  // Shop currently being viewed
  const currentShopId = shopId && shopId !== 'all' ? shopId : ukShopId;

  // All shops except the current one
  const otherShops = shops.filter(s => String(s.shop_id) !== String(currentShopId));

  const resetMoveState = () => {
    setShowMoveList(false);
    setMoveShopIds([]);
  };

  const handleSelectAll = () => {
    if (selectedBrands.length === filteredBrands.length && filteredBrands.length > 0) {
      setSelectedBrands([]);
    } else {
      setSelectedBrands(filteredBrands.map(brand => brand.brand_id));
    }
    setShowActionDropdown(false);
    resetMoveState();
  };

  const handleSelectBrand = (brandId) => {
    if (selectedBrands.includes(brandId)) {
      setSelectedBrands(selectedBrands.filter(id => id !== brandId));
    } else {
      setSelectedBrands([...selectedBrands, brandId]);
    }
    setShowActionDropdown(false);
    resetMoveState();
  };

  const handleToggleMoveShop = (id) => {
    const key = String(id);
    if (moveShopIds.includes(key)) {
      setMoveShopIds(moveShopIds.filter(x => x !== key));
    } else {
      setMoveShopIds([...moveShopIds, key]);
    }
  };

  const handleMoveSelected = async () => {
    if (selectedBrands.length === 0) {
      showWarningAlert('Please select brands first');
      return;
    }
    if (moveShopIds.length === 0) {
      showWarningAlert('Please select at least one shop');
      return;
    }

    setMoveLoading(true);

    const brandsToCopy = brands.filter(b => selectedBrands.includes(b.brand_id));
    let successCount = 0;
    let failedCount = 0;

    try {
      for (const targetId of moveShopIds) {
        // Implant companies of the target shop (to match by name)
        let targetCompanies = [];
        try {
          const compRes = await fetch(`${API_BASE_URL}/implant-companies/?shop_id=${targetId}`);
          if (compRes.ok) {
            const compData = await compRes.json();
            targetCompanies = Array.isArray(compData) ? compData : [];
          }
        } catch (err) {}

        for (const brand of brandsToCopy) {
          const body = {
            brand_name: brand.brand_name,
            shop_id: parseInt(targetId)
          };

          const companyName = brand.implant_company?.company_name;
          if (companyName) {
            const match = targetCompanies.find(
              c => c.company_name?.toLowerCase() === companyName.toLowerCase()
            );
            if (match) body.implant_company_id = match.company_id;
          }

          try {
            const response = await fetch(`${API_BASE_URL}/brands/create/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            });
            if (response.ok) successCount++;
            else failedCount++;
          } catch (err) {
            failedCount++;
          }
        }
      }

      if (successCount > 0 && failedCount === 0) {
        showSuccessAlert(`${successCount} brand(s) copied successfully!`);
      } else if (successCount > 0) {
        showWarningAlert(`${successCount} copied, ${failedCount} failed (maybe already exist).`);
      } else {
        showErrorAlert('Could not copy brands (maybe they already exist in those shops).');
      }

      setSelectedBrands([]);
      setShowActionDropdown(false);
      resetMoveState();
      await fetchBrands();
    } finally {
      setMoveLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedBrands.length === 0) {
      showWarningAlert('Please select brands to delete');
      return;
    }

    const result = await showDeleteConfirmation(null, true, selectedBrands.length);

    if (result.isConfirmed) {
      try {
        let deletedCount = 0;
        for (const brandId of selectedBrands) {
          const response = await fetch(`${API_BASE_URL}/brands/${brandId}/delete/`, {
            method: 'DELETE',
          });
          if (response.ok || response.status === 204) deletedCount++;
        }
        showSuccessAlert(`${deletedCount} brand(s) deleted successfully!`);
        setSelectedBrands([]);
        setShowActionDropdown(false);
        await fetchBrands();
      } catch (error) {
        showErrorAlert('Failed to delete some brands. Please try again.');
      }
    }
  };

  const handleDeleteSingleBrand = async (brand) => {
    const result = await showDeleteConfirmation(brand.brand_name);

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${API_BASE_URL}/brands/${brand.brand_id}/delete/`, {
          method: 'DELETE',
        });
        if (response.ok || response.status === 204) {
          showSuccessAlert('Brand deleted successfully!');
          setIsModalOpen(false);
          setEditingBrand(null);
          setSelectedBrands(selectedBrands.filter(id => id !== brand.brand_id));
          await fetchBrands();
        } else {
          showErrorAlert('Failed to delete brand');
        }
      } catch (error) {
        showErrorAlert('Network error. Please try again.');
      }
    }
  };

  const handleClearAll = () => {
    setSelectedBrands([]);
    setShowActionDropdown(false);
    resetMoveState();
  };

  const handleOpenModalForNew = () => {
    setEditingBrand(null);
    setBrandName('');
    setSelectedCompanyId('');
    setCompanySearchTerm('');
    setSelectedShopId('');
    setIsModalOpen(true);
    setShowCompanyDropdown(false);
  };

  const handleOpenModalForEdit = (brand) => {
    setEditingBrand(brand);
    setBrandName(brand.brand_name);
    setSelectedCompanyId(brand.implant_company?.company_id?.toString() || '');
    setCompanySearchTerm('');
    setSelectedShopId(brand.shop?.shop_id?.toString() || '');
    setIsModalOpen(true);
    setShowCompanyDropdown(false);
  };

  const handleBrandNameChange = (e) => setBrandName(e.target.value);

  const handleCompanySearchChange = (e) => {
    setCompanySearchTerm(e.target.value);
    setShowCompanyDropdown(true);
  };

  const handleCompanyClick = () => {
    if (companyInputRef.current) {
      const rect = companyInputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX
      });
    }
    setShowCompanyDropdown(!showCompanyDropdown);
  };

  const handleCompanySelect = (company) => {
    setSelectedCompanyId(company.company_id.toString());
    setCompanySearchTerm('');
    setShowCompanyDropdown(false);
  };

  const handleShopChange = async (e) => {
    const newShopId = e.target.value;
    setSelectedShopId(newShopId);
    setSelectedCompanyId('');

    if (newShopId) {
      await fetchImplantCompanies(newShopId);
    }
  };

  const handleSave = async () => {
    if (!brandName.trim()) {
      showWarningAlert('Please enter Brand Name');
      return;
    }

    let targetShopId;
    if (editingBrand) {
      targetShopId = editingBrand.shop?.shop_id;
    } else if (shopId && shopId !== 'all') {
      targetShopId = parseInt(shopId);
    } else if (selectedShopId) {
      targetShopId = parseInt(selectedShopId);
    } else {
      targetShopId = ukShopId;
    }

    if (!targetShopId) {
      showWarningAlert('Please select a shop');
      return;
    }

    setSaveLoading(true);

    try {
      const requestBody = {
        brand_name: brandName.trim(),
        shop_id: targetShopId
      };
      if (selectedCompanyId) requestBody.implant_company_id = parseInt(selectedCompanyId);

      if (editingBrand) {
        const response = await fetch(`${API_BASE_URL}/brands/${editingBrand.brand_id}/update/`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });
        const data = await response.json();
        if (response.ok) {
          showSuccessAlert('Brand updated successfully!');
          setIsModalOpen(false);
          setEditingBrand(null);
          setBrandName('');
          setSelectedCompanyId('');
          setSelectedShopId('');
          await fetchBrands();
        } else {
          showErrorAlert(data.error || 'Failed to update brand');
        }
      } else {
        const response = await fetch(`${API_BASE_URL}/brands/create/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });
        const data = await response.json();
        if (response.ok) {
          showSuccessAlert('Brand added successfully!');
          setIsModalOpen(false);
          setBrandName('');
          setSelectedCompanyId('');
          setSelectedShopId('');
          await fetchBrands();
        } else {
          showErrorAlert(data.error || 'Failed to add brand');
        }
      }
    } catch (error) {
      showErrorAlert('Network error. Please check if backend server is running.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    if (editingBrand) {
      const result = await showDeleteConfirmation(editingBrand.brand_name);
      if (result.isConfirmed) {
        try {
          const response = await fetch(`${API_BASE_URL}/brands/${editingBrand.brand_id}/delete/`, {
            method: 'DELETE',
          });
          if (response.ok || response.status === 204) {
            showSuccessAlert('Brand deleted successfully!');
            setIsModalOpen(false);
            setEditingBrand(null);
            setSelectedBrands(selectedBrands.filter(id => id !== editingBrand.brand_id));
            await fetchBrands();
          } else {
            showErrorAlert('Failed to delete brand');
          }
        } catch (error) {
          showErrorAlert('Network error. Please try again.');
        }
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBrand(null);
    setBrandName('');
    setSelectedCompanyId('');
    setCompanySearchTerm('');
    setSelectedShopId('');
    setShowCompanyDropdown(false);
  };

  const handleClickOutside = (e) => {
    if (showActionDropdown && !e.target.closest('.action-dropdown-container')) {
      setShowActionDropdown(false);
      resetMoveState();
    }
    if (showCompanyDropdown && companyDropdownRef.current &&
        !companyDropdownRef.current.contains(e.target) &&
        e.target !== companyInputRef.current) {
      setShowCompanyDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showActionDropdown, showCompanyDropdown]);

  const isOperationInProgress = saveLoading || moveLoading;

  const getSelectedShopName = () => {
    if (selectedShopId) {
      const shop = shops.find(s => s.shop_id === parseInt(selectedShopId));
      return shop ? shop.shop_name : '';
    }
    return '';
  };

  return (
    <div className="brands-main-container">

      {/* Heading + Search + Buttons */}
      <div className="brands-action-section">
        <div className="brands-heading">
          <h2>
            <TbBrandCodepen className="brands-heading-icon" />
            Brands Management
          </h2>
        </div>

        <div className="brands-action-right">
          <div className="brands-search-wrapper">
            <FaSearch className="brands-search-icon" />
            <input
              type="text"
              placeholder="Search by Brand Name..."
              value={searchTerm}
              onChange={handleSearch}
              className="brands-search-input"
              disabled={isOperationInProgress}
            />
          </div>

          <div className="brands-action-buttons">
            <button className="brands-select-all-btn" onClick={handleSelectAll} disabled={isOperationInProgress}>
              <FaCheckDouble />
              <span>{selectedBrands.length === filteredBrands.length && filteredBrands.length > 0 ? 'Deselect All' : 'Select All'}</span>
            </button>
            <button className="brands-add-btn" onClick={handleOpenModalForNew} disabled={isOperationInProgress}>
              <FaPlus />
              <span>ADD BRAND</span>
            </button>
          </div>
        </div>
      </div>

      {selectedBrands.length > 0 && (
        <div className="selected-summary">
          <div className="summary-content">
            <span className="summary-icon">✓</span>
            <span className="summary-text">
              {selectedBrands.length} brand(s) selected
            </span>
            <div className="action-dropdown-container">
              <button
                className="action-dropdown-btn"
                onClick={() => {
                  setShowActionDropdown(!showActionDropdown);
                  resetMoveState();
                }}
              >
                <FaEllipsisV />
                <span>Actions</span>
              </button>
              {showActionDropdown && (
                <div className="action-dropdown-menu">
                  <button
                    className="dropdown-item"
                    onClick={() => setShowMoveList(!showMoveList)}
                  >
                    <FaChevronRight className={`move-arrow ${showMoveList ? 'move-arrow-open' : ''}`} />
                    <span>Move To</span>
                  </button>

                  {showMoveList && (
                    <div className="move-shops-panel">
                      <div className="move-shops-list">
                        {otherShops.length > 0 ? (
                          otherShops.map(shop => (
                            <label key={shop.shop_id} className="move-shop-item">
                              <input
                                type="checkbox"
                                checked={moveShopIds.includes(String(shop.shop_id))}
                                onChange={() => handleToggleMoveShop(shop.shop_id)}
                                disabled={moveLoading}
                              />
                              <span>{shop.shop_name}</span>
                            </label>
                          ))
                        ) : (
                          <div className="move-no-shops">No other shops</div>
                        )}
                      </div>
                      {otherShops.length > 0 && (
                        <button
                          className="move-apply-btn"
                          onClick={handleMoveSelected}
                          disabled={moveLoading || moveShopIds.length === 0}
                        >
                          {moveLoading ? 'Copying...' : `Copy to ${moveShopIds.length} shop(s)`}
                        </button>
                      )}
                    </div>
                  )}

                  <button
                    className="dropdown-item delete-item"
                    onClick={handleDeleteSelected}
                  >
                    <RiDeleteBinLine />
                    <span>Delete Selected ({selectedBrands.length})</span>
                  </button>
                </div>
              )}
            </div>
            <button className="clear-selection" onClick={handleClearAll}>
              Clear all
            </button>
          </div>
        </div>
      )}

      {/* TABLE - City column removed */}
      <div className="brands-table-container">
        <div className="brands-table-wrapper">
          <table className="brands-table">
            <thead>
              <tr>
                <th style={{ width: '50px' }}>
                  <input
                    type="checkbox"
                    checked={selectedBrands.length === filteredBrands.length && filteredBrands.length > 0}
                    onChange={handleSelectAll}
                    disabled={isOperationInProgress}
                  />
                </th>
                <th>BRAND NAME</th>
                <th>IMPLANT COMPANY</th>
                <th>SHOP</th>
                <th style={{ width: '100px' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {/* Nothing is shown until the first load is done, so "No brands found" never flashes */}
              {isLoading ? (
                <tr>
                  <td colSpan="5" style={{ height: '200px' }}></td>
                </tr>
              ) : filteredBrands.length > 0 ? (
                filteredBrands.map((brand) => (
                  <tr key={brand.brand_id} className={selectedBrands.includes(brand.brand_id) ? 'brands-selected-row' : ''}>
                    <td className="brands-checkbox-cell">
                      <input
                        type="checkbox"
                        checked={selectedBrands.includes(brand.brand_id)}
                        onChange={() => handleSelectBrand(brand.brand_id)}
                        disabled={isOperationInProgress}
                      />
                    </td>
                    <td className="brands-name-cell">
                      <span className="brands-name">{brand.brand_name}</span>
                    </td>
                    <td className="brands-company-cell">
                      <span className="brands-company-text">{getCompanyDisplayName(brand)}</span>
                    </td>
                    <td className="brands-shop-cell">
                      <span className="brands-shop-badge">{getShopDisplayName(brand)}</span>
                    </td>
                    <td className="brands-actions-cell">
                      <button
                        className="brands-edit-btn"
                        onClick={() => handleOpenModalForEdit(brand)}
                        title="Edit Brand"
                        disabled={isOperationInProgress}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="brands-delete-btn"
                        onClick={() => handleDeleteSingleBrand(brand)}
                        title="Delete Brand"
                        disabled={isOperationInProgress}
                      >
                        <RiDeleteBin5Line />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="brands-no-data-row">
                    <div className="brands-no-data">
                      <FaTags className="brands-no-data-icon" />
                      <p>No brands found for {shopName || 'this shop'}.</p>
                      <button className="brands-add-first-btn" onClick={handleOpenModalForNew}>
                        <FaPlus /> Add Your First Brand
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL - City field removed */}
      {isModalOpen && (
        <div className="brands-modal-overlay" onClick={handleCloseModal}>
          <div className="brands-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="brands-modal-header">
              <h3>{editingBrand ? 'Edit Brand' : 'Add New Brand'}</h3>
              <button className="brands-modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="brands-modal-body">
              <form className="brands-form">
                {(!shopId || shopId === 'all') && !editingBrand && (
                  <div className="brands-form-row">
                    <label className="brands-form-label">SHOP:</label>
                    <select
                      className="brands-shop-select-dropdown"
                      value={selectedShopId}
                      onChange={handleShopChange}
                      disabled={isOperationInProgress}
                    >
                      <option value="">-- Select a Shop --</option>
                      {shops.map(shop => (
                        <option key={shop.shop_id} value={shop.shop_id}>
                          {shop.shop_name} (ID: {shop.shop_id})
                        </option>
                      ))}
                    </select>
                    {selectedShopId && (
                      <div className="brands-shop-display-under">
                        <span>{getSelectedShopName()}</span>
                      </div>
                    )}
                  </div>
                )}

                {(shopId && shopId !== 'all' || editingBrand) && (
                  <div className="brands-form-row">
                    <div className="brands-shop-info-display">
                      <span className="brands-shop-display-name">
                        {editingBrand && editingBrand.shop
                          ? `${editingBrand.shop.shop_name} (ID: ${editingBrand.shop.shop_id})`
                          : `${shopName} (ID: ${shopId})`
                        }
                      </span>
                    </div>
                  </div>
                )}

                <div className="brands-form-row">
                  <label className="brands-form-label">IMPLANT COMPANY:</label>
                  <div className="brands-input-wrapper">
                    <input
                      ref={companyInputRef}
                      type="text"
                      value={selectedCompanyId ? getSelectedCompanyName() : companySearchTerm}
                      onChange={handleCompanySearchChange}
                      onClick={handleCompanyClick}
                      placeholder="Search and select implant company"
                      className="brands-input"
                      disabled={isOperationInProgress}
                      readOnly={!!selectedCompanyId}
                    />
                    {selectedCompanyId && (
                      <button
                        type="button"
                        className="brands-clear-company-btn"
                        onClick={() => {
                          setSelectedCompanyId('');
                          setCompanySearchTerm('');
                        }}
                      >
                        ×
                      </button>
                    )}
                    <button
                      type="button"
                      className="brands-dropdown-toggle-btn"
                      onClick={handleCompanyClick}
                      disabled={isOperationInProgress}
                    >
                      <FaChevronDown className={`brands-dropdown-arrow ${showCompanyDropdown ? 'brands-rotated' : ''}`} />
                    </button>
                  </div>
                </div>

                <div className="brands-form-row">
                  <label className="brands-form-label">BRAND NAME:</label>
                  <input
                    type="text"
                    value={brandName}
                    onChange={handleBrandNameChange}
                    placeholder="Enter brand name"
                    autoFocus
                    className="brands-input"
                    disabled={isOperationInProgress}
                  />
                </div>
              </form>
            </div>
            <div className="brands-modal-footer">
              <button className="brands-btn-cancel" onClick={handleCloseModal} disabled={isOperationInProgress}>
                Cancel
              </button>
              {editingBrand && (
                <button className="brands-btn-delete" onClick={handleDelete} disabled={isOperationInProgress}>
                  Delete
                </button>
              )}
              <button className="brands-btn-save" onClick={handleSave} disabled={isOperationInProgress}>
                {isOperationInProgress ? 'Saving...' : (editingBrand ? 'Update Brand' : 'Save Brand')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCompanyDropdown && (
        <div
          className="brands-company-dropdown-outside"
          ref={companyDropdownRef}
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            minWidth: '300px',
            maxWidth: '400px',
            zIndex: 10001
          }}
        >
          <div className="brands-company-dropdown-list">
            {filteredCompanies.length > 0 ? (
              filteredCompanies.map((company) => (
                <button
                  key={company.company_id}
                  type="button"
                  className="brands-company-dropdown-item"
                  onClick={() => handleCompanySelect(company)}
                >
                  <span>{company.company_name}</span>
                  {company.shop && (
                    <span className="brands-company-shop-tag">({company.shop.shop_name})</span>
                  )}
                </button>
              ))
            ) : (
              <div className="brands-no-companies-message">No implant companies found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Brands;